import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { TerraDraw, TerraDrawPolygonMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import MapScriptLoader from 'bundles/connect/helpers/map_script_loader';

import {
  GoogleMap,
  Polygon,
  StandaloneSearchBox
} from '@react-google-maps/api';

const DEFAULT_CENTER = { lat: 38, lng: -92 };
const DEFAULT_ZOOM = 4;
// The TerraDraw Google Maps adapter requires the map container div to have an id.
const MAP_CONTAINER_ID = 'requirements-polygon-map';

export default function PolygonMap({ fitBoundsDelay = 0, geoJSON, googleMapsApiKey, onPolygonComplete }) {
  let polygon;
  if (!isEmpty(geoJSON)) {
    const coordinates = geoJSON.coordinates[0];
    polygon = coordinates.map(c => ({ lat: c[1], lng: c[0] }));
  }

  const [map, setMap] = useState(null);
  const [searchBox, setSearchBox] = useState(null);
  const onPolygonCompleteRef = useRef(onPolygonComplete);
  onPolygonCompleteRef.current = onPolygonComplete;

  useEffect(() => {
    if (!map || !polygon) return;

    const bounds = new google.maps.LatLngBounds();
    polygon.map(coord => bounds.extend(coord));

    if (fitBoundsDelay === 0) {
      map.fitBounds(bounds);
    } else {
      setTimeout(() => {
        map.fitBounds(bounds);
      }, fitBoundsDelay);
    }
  }, [map, geoJSON]);

  const handlePlacesChanged = () => {
    const place = searchBox.getPlaces()[0];

    if (!place || !place.geometry || !map) return;

    if (place.geometry.viewport) {
      map.fitBounds(place.geometry.viewport);
    } else {
      map.setCenter(place.geometry.location);
      map.setZoom(17);
    }
  };

  useEffect(() => {
    if (!map) return;

    const draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({ lib: google.maps, map, coordinatePrecision: 9 }),
      modes: [new TerraDrawPolygonMode()]
    });

    draw.on('finish', (id, context) => {
      if (context.action !== 'draw') return;

      const feature = draw.getSnapshot().find(f => f.id === id);
      draw.clear();
      if (!feature) return;

      const coordinates = feature.geometry.coordinates[0];
      // We only care about polygons with area. Rings are closed, so 3 points is 4 coordinates.
      if (coordinates.length < 4) return;

      onPolygonCompleteRef.current({
        type: 'Polygon',
        coordinates: [coordinates] // intentional double wrapping
      });
    });

    // The adapter needs the map's panes, which don't exist until the map has rendered once.
    const idleListener = google.maps.event.addListenerOnce(map, 'idle', () => {
      draw.start();
      draw.setMode('polygon');
    });

    return () => {
      google.maps.event.removeListener(idleListener);
      if (draw.enabled) draw.stop();
    };
  }, [map]);

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  return (
    <MapScriptLoader googleMapsApiKey={googleMapsApiKey}>
      <GoogleMap
        center={DEFAULT_CENTER}
        id={MAP_CONTAINER_ID}
        mapContainerStyle={{ width: '100%', height: '400px' }}
        options={{ mapTypeControl: false }}
        zoom={DEFAULT_ZOOM}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        <StandaloneSearchBox
          onLoad={ref => setSearchBox(ref)}
          onPlacesChanged={handlePlacesChanged}
        >
          <input
            placeholder="Enter a location"
            style={{
              boxSizing: 'border-box',
              border: '1px solid transparent',
              width: '240px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '3px',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
              fontSize: '14px',
              outline: 'none',
              textOverflow: 'ellipses',
              position: 'absolute',
              left: '10px',
              top: '10px'
            }}
            type="text"
          />
        </StandaloneSearchBox>
        {polygon && <Polygon path={polygon} />}
      </GoogleMap>
    </MapScriptLoader>
  );
}

PolygonMap.propTypes = {
  fitBoundsDelay: PropTypes.number,
  geoJSON: PropTypes.object,
  googleMapsApiKey: PropTypes.string.isRequired,
  onPolygonComplete: PropTypes.func.isRequired,
};
